import { Alert, Button, HFlow, Icon, Text, Tooltip, useStyles, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import CheckPermission from 'components/auth/CheckPermission'
import { useErrorHandler } from 'components/error'
import { InfoIcon } from 'components/InfoIcon'
import {
  useConfiguracaoCertificadoDignidadeMenstrualQuery,
  useRecusarCadastroCidadaoMutation,
} from 'graphql/hooks.generated'
import { CidadaoListingQuery } from 'graphql/types.generated'
import { useLoginGovBr } from 'hooks/useLoginGovBr'
import { useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'

import { ModalInformarLoginGovBr } from '../../components/ModalInformarLoginGovBr'
import { ModalRecusaCadastro } from '../../components/ModalRecusaCadastro'
import { CidadaoCadsusItemType } from '../../create/cadsus/types/model-cadsus'
import { useNeedLoginGovBrToCadsus } from '../../hooks/useNeedLoginGovBrToCadsus'
import { CidadaoUrlParams } from '../../types/cidadao-urlParams'
import { CidadaoCadsusListingItem } from './CidadaoCadsusListItem'
import { CidadaoListingItem } from './CidadaoListingItem'

interface CidadaoListingViewProps {
  result: CidadaoListingQuery
  resultCadsus: CidadaoCadsusItemType[]
  loadingCadsus: boolean
  hasCadsusCalled: boolean
  onBuscarClicked(): void
  refetchLocal(): void
  hasInternetConnection: boolean
  hasCadsusConnection: boolean
  hasCertificadoCadsus: boolean
  cidadaoUrlParams: CidadaoUrlParams
}

export function CidadaoListItemView(props: Readonly<CidadaoListingViewProps>) {
  const { result, resultCadsus, hasInternetConnection, hasCadsusConnection, hasCertificadoCadsus } = props

  const alert = useAlert()
  const handleRejection = useErrorHandler()

  const match = useRouteMatch()
  const history = useHistory()
  const totalElements = props.result?.cidadaos?.content.length
  const totalElementsCadsus = props.resultCadsus?.length
  const hasMoreResults = props.result?.cidadaos?.content.length > 50
  const isOnline = props.hasInternetConnection && props.hasCadsusConnection
  const { theme } = useStyles()
  const { isAccessTokenGovBrAlive } = useLoginGovBr()
  const { data: certificadoDignidadeMenstrual } = useConfiguracaoCertificadoDignidadeMenstrualQuery()
  const isConfiguracaoPDMAtiva = certificadoDignidadeMenstrual?.configuracaoCertificadoHabilitado?.farmaciaHabilitado
  const [recusarCadastroCidadao] = useRecusarCadastroCidadaoMutation()

  const { needLoginGovBr: deveAparecerModalGovBr } = useNeedLoginGovBrToCadsus()

  const [modalInformarLoginGovBrOpen, setModalInformarLoginGovBrOpen] = useState(false)
  const [modalRecusaCadastroOpen, setModalRecusaCadastroOpen] = useState(false)

  const continueAdicionarCidadao = () => {
    history.push(`${match.url}/create${history.location.search}`)
  }

  const onClickAdicionarCidadao = () => {
    if (deveAparecerModalGovBr) {
      setModalInformarLoginGovBrOpen(true)
    } else {
      continueAdicionarCidadao()
    }
  }

  const onClickRecusaCadastro = () => {
    setModalRecusaCadastroOpen(true)
  }

  const onCloseRecusaCadastroModal = () => {
    setModalRecusaCadastroOpen(false)
  }

  const onConfirmRecusaCadastro = () => {
    return recusarCadastroCidadao({ variables: {} })
      .then(() => {
        alert('success', 'Recusa de cadastro registrada.')
        setModalRecusaCadastroOpen(false)
      })
      .catch(handleRejection)
  }

  const pluralizeCadastrosEncontrados = (total?: number) => {
    const totalEncontrados = total ?? 0
    return totalEncontrados === 1
      ? totalEncontrados + ' cadastro encontrado'
      : totalEncontrados + ' cadastros encontrados'
  }

  return (
    <>
      {result != null ? (
        <VFlow vSpacing={0.5}>
          <HFlow alignItems='baseline'>
            <Text fontWeight='bold' fontSize={1.5}>
              Cadastros na base local
            </Text>
            {totalElements > 0 && !hasMoreResults ? (
              <Text fontSize={1}>{pluralizeCadastrosEncontrados(totalElements)}</Text>
            ) : null}
          </HFlow>
          {totalElements > 0 &&
            !hasMoreResults &&
            result.cidadaos?.content?.map((result) => (
              <CidadaoListingItem
                data={result}
                key={result.id}
                refetchLocal={props.refetchLocal}
                cidadaoUrlParams={props.cidadaoUrlParams}
                isAccessTokenGovBrAlive={isAccessTokenGovBrAlive}
                isConfiguracaoPDMAtiva={isConfiguracaoPDMAtiva}
              />
            ))}
          {!totalElements && !hasMoreResults && <Text>Nenhum resultado encontrado.</Text>}
          {hasMoreResults && <Text>Refine a busca incluindo mais informações.</Text>}

          <HFlow alignItems='baseline' style={{ marginTop: '1rem' }}>
            <HFlow hSpacing={0.5} alignItems='center'>
              <Text fontWeight='bold' fontSize={1.5}>
                Cadastros na base nacional
              </Text>
              <InfoIcon
                icon='infoCircleFilled'
                text={
                  hasCertificadoCadsus
                    ? 'Para pesquisar na base nacional, é necessário configurar o certificado e estar logado através do gov.br.'
                    : 'Para pesquisar na base nacional, é necessário configurar o certificado e estar logado através do gov.br. Entre em contato com o administrador municipal.'
                }
              />
            </HFlow>

            {totalElementsCadsus > 0 ? (
              <Text fontSize={1}>{pluralizeCadastrosEncontrados(totalElementsCadsus)}</Text>
            ) : null}
          </HFlow>

          {!hasInternetConnection ? (
            <Alert inline type='warning'>
              Não foi possível buscar na base nacional, pois a instalação está offline. Entre em contato com o
              responsável pela instalação para mais informações.
            </Alert>
          ) : (
            !hasCadsusConnection && (
              <Alert inline type='warning'>
                Não foi possível buscar na base nacional, pois a conexão com o serviço está desabilitada. Entre em
                contato com o responsável pela instalação para mais informações.
              </Alert>
            )
          )}

          {isOnline && props.result && !props.resultCadsus && (
            <Tooltip
              text={
                hasCertificadoCadsus
                  ? 'Para pesquisar na base nacional, é necessário configurar o certificado e estar logado através do gov.br.'
                  : 'Para pesquisar na base nacional, é necessário configurar o certificado e estar logado através do gov.br. Entre em contato com o administrador municipal.'
              }
            >
              <Button
                size='large'
                block
                loading={props.loadingCadsus}
                onClick={props.onBuscarClicked}
                disabled={!hasCertificadoCadsus}
              >
                <Icon icon='zoomOutline' style={{ marginRight: '0.5rem' }} />
                Pesquisar na base nacional
              </Button>
            </Tooltip>
          )}
          {props.resultCadsus?.map((result) => (
            <CidadaoCadsusListingItem data={result} key={result.cns} />
          ))}
          {resultCadsus != null && !resultCadsus?.length && <Text>Nenhum resultado encontrado.</Text>}
          <CheckPermission permission={Permissions.gestaoDeCadastros.visualizarCadastroIndividual.cadastrarEEditar}>
            {props.result != null && (
              <>
                <div
                  style={{ borderTop: `1px solid ${theme.pallete.divider}`, marginTop: '1.6rem', marginBottom: '1rem' }}
                />
                <HFlow justifyContent='flex-end'>
                  <Button kind='normal' skin='default' size='medium' onClick={onClickRecusaCadastro}>
                    Recusa de cadastro
                  </Button>
                  <Button kind='primary' skin='default' size='medium' onClick={onClickAdicionarCidadao}>
                    Adicionar cadastro
                  </Button>
                </HFlow>
              </>
            )}
          </CheckPermission>
          <ModalInformarLoginGovBr
            open={modalInformarLoginGovBrOpen}
            onClose={() => setModalInformarLoginGovBrOpen(false)}
            onConfirm={continueAdicionarCidadao}
          />
          <ModalRecusaCadastro
            open={modalRecusaCadastroOpen}
            onClose={onCloseRecusaCadastroModal}
            onConfirm={onConfirmRecusaCadastro}
          />
        </VFlow>
      ) : (
        <VFlow style={{ marginTop: '1rem' }}>
          <Text fontWeight='bold' fontSize={1}>
            Busque um cadastro por nome, CPF, CNS ou demais informações.
          </Text>
        </VFlow>
      )}
    </>
  )
}
